import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import WithProcessError from 'hocs/WithProcessError';
import PropTypes from 'prop-types';

import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import FormGroup from 'components/FormGroup/FormGroup';
import FormItem from 'components/FormItem/FormItem';
import IconButton from 'components/IconButton/IconButton';
import Input from 'components/Input/Input';
import Loader from 'components/Loader/Loader';
import Panel from 'components/Panel/Panel';
import PanelHeader from 'components/PanelHeader/PanelHeader';
import PaymentForm from 'components/PaymentForm/PaymentForm';
import Select from 'components/Select/Select';
import Snackbar from 'components/Snackbar/Snackbar';

import AdditionalField from './AdditionalField/AdditionalField';
import { previewPanelStyle } from '../../constants';
import { paymentsCounts, paymentsPeriods, paymentsTypes } from './paymentsParameters';

import Add24 from 'icons/Add24';
import Check24 from 'icons/Check24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import { validateName, validateNumber, validateReqiured } from 'utils/validate';

function PageConstructor(props) {
    const auth = useAuth();
    const api = useApi();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data, isRecurrent = data?.is_recurrent || false, onBackClick, processError } = props;
    const isCreatingNewPage = data.id === undefined;

    const [paymentServices, setPaymentServices] = React.useState(null);

    const [settings, setSettings] = React.useState({
        title: {
            value: data.title || 'Новый продукт',
            error: null,
            required: true,
            type: 'string',
        },
        default_regular_amount: {
            value: data.default_regular_amount ?? 149,
            error: null,
            required: true,
            type: 'number',
        },
        default_first_amount: {
            value: data.default_first_amount ?? 0,
            error: null,
            type: 'number',
        },
        definition: {
            value: data.definition || 'Оплата товара или услуги',
            error: null,
            required: true,
            type: 'string',
        },
        payment_object: {
            value: _parsePaymentObject(data.payment_object),
            type: 'string',
        },
        once_per_days: {
            value: data.once_per_days || 30,
            type: 'number',
        },
        is_unlimited: {
            value: data.is_unlimited ?? true,
            type: 'boolean',
        },
        default_payments_number: {
            value: data.default_payments_number || 1,
            error: null,
            required: true,
            type: 'number',
        },
        terminal_id: {
            value: data.terminal_id || undefined,
            error: null,
            type: 'number',
        },
        is_recurrent: {
            value: isRecurrent,
            error: null,
            type: 'boolean',
        },
        default_creditable: {
            value: data.default_creditable || false,
            error: null,
            type: 'boolean',
        },
    });
    const [additionalFields, setAdditionalFields] = React.useState(_parseAdditionalFields(data.external_fields) || []);
    const [loading, setLoading] = React.useState(false);
    const [snack, setSnack] = React.useState(null);

    function _parsePaymentObject(paymentObject) {
        if (paymentObject === undefined) {
            return 'service';
        }

        const isExists = paymentsTypes.find((type) => paymentObject === type.value);

        if (!isExists) {
            console.warn('Provided payment object does not exists.', paymentObject);
        }

        return isExists ? paymentObject : 'service';
    }

    /**
     * Parse additional fields for costructor.
     */
    function _parseAdditionalFields(fields) {
        if (!fields) return [];

        return fields.map((field) => ({
            field_name: {
                value: field.field_name,
                error: null,
            },
            is_necessary: field.is_necessary,
            id: field.id,
        }));
    }

    /**
     * Prepare additional fields for server.
     */
    function _prepareAdditionalFields() {
        return additionalFields.map((field) => ({
            field_name: field.field_name.value,
            is_necessary: field.is_necessary,
            id: field.id,
        }));
    }

    /**
     * Proccess settings item to parse it to needed type.
     * @param {any} item Settings item to proccess.
     * @returns processed item in a correct type.
     */
    function _processSettingsItem(item) {
        const { value, type } = item;
        switch (type) {
            case 'string':
                return value.length > 0 ? value : null;
            case 'number':
                return isNaN(Number(value)) ? value : Number(value);
            case 'boolean':
                return Boolean(value);
            default:
                return value.toString();
        }
    }

    /**
     * Validated input field.
     * @param {any} value Input field value
     * @param {string} name Input field name
     * @returns
     */
    function validateInputField(value, name) {
        const field = settings[name];

        if (field !== undefined) {
            if (field.required && validateReqiured(value) !== null) {
                return validateReqiured(value);
            }

            if (name === 'default_regular_amount' || name === 'default_first_amount') {
                return validateAmount(value, 1, 1000000);
            }

            if (name === 'default_payments_number') {
                return validateNumber(value, 1, 36);
            }
        }
    }

    /**
     * Validate payment amount.
     * @param {string} value input value
     * @returns {object} Null of no errors, or Error object with message.
     */
    function validateAmount(value, min, max) {
        if (isNaN(Number(value))) {
            return new Error('Допустимы только числа');
        }

        if (value === '') return;

        // Check for min value
        if (typeof min === 'number') {
            if (Number(value) < min) {
                return new Error('Не менее ' + min + ' руб.');
            }
        }

        // Check for max value
        if (typeof max === 'number') {
            if (Number(value) > max) {
                return new Error('Не более ' + max + ' руб.');
            }
        }

        return null;
    }

    /**
     * Handles constructor form inputs chages.
     * @param {event} e input change event
     */
    function handleInputChange(e) {
        const input = e.target;
        const inputName = input.name;
        const value = input.value;
        const error = validateInputField(value, inputName);
        setSettings((settings) => ({ ...settings, [inputName]: { ...settings[inputName], value, error } }));
    }

    /**
     * Adds a new field to the additional fields array.
     */
    function handleAddField() {
        const field = {
            field_name: { value: 'Новое поле', error: null },
            is_necessary: false,
        };
        setAdditionalFields((additionalFields) => [...additionalFields, ...[field]]);
    }

    /**
     * Handles additional field label changes.
     * @param {event} e input onChange event
     * @param {number} fieldId id of edited field
     */
    function handleChangeLabel(e, fieldId) {
        const value = e.target.value;
        const error = validateName(value);
        const field_name = { value, error };

        const currentFields = [...additionalFields];
        currentFields.splice(fieldId, 1, { ...currentFields[fieldId], field_name });
        setAdditionalFields(currentFields);
    }

    /**
     * Delete additional field by it's ID.
     * @param {number} fieldIdx ID of the field to delete.
     */
    function handleDeleteField(fieldIdx) {
        const currentFields = [...additionalFields];

        const fieldId = currentFields[fieldIdx]?.id;

        currentFields.splice(fieldIdx, 1);
        // setAdditionalFields(currentFields);
        setAdditionalFields(currentFields);

        if (fieldId !== undefined) {
            console.log('Delete on the server');

            const companyId = auth?.company.id;
            const formId = data.id;

            api.deleteExternalField(companyId, formId, fieldId)
                .then((data) => {
                    if (data?.form_id !== formId) {
                        processError('Не удалось удалить поле');
                    }
                })
                .catch((error) => processError('Не удалось удалить поле', error));
        }
    }

    /**
     * Update payment_object parameter.
     * @param {*} value New payment type.
     */
    function handlePaymentsTypeChange(value) {
        setSettings((settings) => ({ ...settings, payment_object: { ...settings.payment_object, value } }));
    }

    /**
     * Update once_per_days parameter.
     * @param {*} value New payment period.
     */
    function handlePaymentsPeriodChange(value) {
        setSettings((settings) => ({ ...settings, once_per_days: { ...settings.once_per_days, value } }));
    }

    /**
     * Update terminal_id parameter.
     * @param {number} value New terminal id.
     */
    function handleTerminalChange(value) {
        setSettings((prevSettings) => ({
            ...prevSettings,

            terminal_id: {
                ...prevSettings.terminal_id,
                value,
                error: null,
            },
        }));
    }

    function handleDefaultCreditableChange(value) {
        setSettings((prevSettings) => ({
            ...prevSettings,

            default_creditable: {
                ...prevSettings.default_creditable,
                value,
                error: null,
            },
        }));
    }

    /**
     * Update is_unlimited parameter.
     * @param {*} value Is payments limited.
     */
    function handlePaymentsLimit(value) {
        const isUnlimited = value === 'inf';

        if (isUnlimited) {
            setSettings((settings) => ({
                ...settings,
                default_payments_number: { ...settings.default_payments_number, error: null },
            }));
        }
        setSettings((settings) => ({ ...settings, is_unlimited: { ...settings.is_unlimited, value: isUnlimited } }));
    }

    /**
     * Final page constructor form validation.
     * @returns {boolean} Result of validation. True if the form is valid and vice-versa.
     */
    function validateForm() {
        const formErrors = 0;
        // Check settings for errors
        for (const parameter in settings) {
            const error = settings[parameter].error;

            if (error instanceof Error) {
                return false;
            }
        }
        /**
         * Terminal Id does not have any default value.
         * So, we need to check if it filled-up by the user.
         */
        const id = Number(settings.terminal_id.value);

        if (isNaN(id)) {
            setSettings((prevSettings) => ({
                ...prevSettings,

                terminal_id: {
                    ...prevSettings.terminal_id,
                    error: new Error('Выберите терминал'),
                },
            }));

            return false;
        }

        // check for additional fields
        for (const field of additionalFields) {
            if (field.field_name.error instanceof Error) {
                return false;
            }
        }
        // check payment type
        const result = {};
        for (const parameter in settings) {
            result[parameter] = settings[parameter].value;
        }

        return formErrors === 0;
    }

    /**
     * Handles "Create Form" button click.
     */
    function handleDoneClick() {
        const isValid = validateForm();

        if (!isValid) return;

        const formSettings = {};
        for (const key in settings) {
            formSettings[key] = _processSettingsItem(settings[key]);
        }

        if (!formSettings.default_first_amount) {
            formSettings.default_first_amount = formSettings.default_regular_amount;
        }
        formSettings.external_fields = _prepareAdditionalFields();

        if (data.id !== undefined) {
            updatePaymentPage(formSettings);
        } else {
            createPaymentPage(formSettings);
        }
    }

    /**
     * Create a new payment form on the server.
     * @param {object} settings Payment form settings.
     * @returns Created form.
     */
    function createPaymentPage(settings) {
        setLoading(true);

        const companyId = auth?.company.id;
        api.createForm(companyId, settings)
            .then((data) => {
                if (data.id !== undefined) {
                    navigate('/pages', {
                        state: {
                            pageId: data.id,
                        },
                    });
                } else {
                    alert('Не удалось создать страницу. Попробуйте снова.');
                }
            })
            .catch((error) => processError('Не удалось создать страницу оплаты', error))
            .finally(() => {
                setLoading(false);
            });
    }

    /**
     * Update payment page on the server.
     * @param {object} settings New payment page settings.
     */
    function updatePaymentPage(settings) {
        setLoading(true);
        const formId = data.id;
        const companyId = auth?.company.id;
        api.updateForm(companyId, formId, settings)
            .then((data) => {
                const updatedExternalFields = _parseAdditionalFields(data.external_fields);
                setAdditionalFields(updatedExternalFields);
                onPaymentPageUpdated();
            })
            .catch((error) => processError('Не удалось сохранить изменения', error))
            .finally(() => {
                setLoading(false);
            });
    }

    /**
     * Show Update Successful snackbar on form update.
     */
    function onPaymentPageUpdated() {
        if (snack) return;
        setSnack(
            <Snackbar text={t('productConstructor.snackbar.success')} delay={1500} onClosed={handleSnackbarClosed} />
        );
    }

    function handleSnackbarClosed() {
        setSnack(null);
    }

    const isCreditAvailable = () => {
        if (settings.is_recurrent.value === true) {
            return false;
        }

        const currentTerminalId = settings.terminal_id.value;

        if (currentTerminalId) {
            const paymentService = paymentServices?.find((ps) => ps.value === currentTerminalId);

            return paymentService && paymentService.type === 'Yookassa';
        }
    };

    /**
     * Get available payment services and connected terminals.
     */
    React.useEffect(() => {
        const companyId = auth.company?.id;
        api.getPaymentServices(companyId)
            .then((result) => {
                if (result && Array.isArray(result.payment_services)) {
                    const paymentServices = result.payment_services.map((service) => {
                        return {
                            type: service.type,
                            label: service.name,
                            value: service.terminal_id,
                        };
                    });

                    const activePaymentServices = paymentServices.filter((ps) => ps.value);

                    const yooKassaTerminal = activePaymentServices.find((ps) => ps.type === 'Yookassa');

                    if (yooKassaTerminal && yooKassaTerminal.value) {
                        const terminalId = yooKassaTerminal.value;

                        api.getTerminal(companyId, terminalId)
                            .then((result) => {
                                /**
                                 * If terminal is NOT ready for usage, remove payment method.
                                 */
                                if (!result.ready) {
                                    const filteredPaymentServices = activePaymentServices.filter(
                                        (ps) => ps.type !== 'Yookassa'
                                    );

                                    setPaymentServices(filteredPaymentServices);
                                } else {
                                    setPaymentServices(activePaymentServices);
                                }
                            })
                            .catch((error) => {
                                processError('Не удалось получить данные о терминале', error);
                            });
                    } else {
                        setPaymentServices(activePaymentServices);

                        setSettings((prevSettings) => ({
                            ...prevSettings,

                            terminal_id: {
                                ...prevSettings.terminal_id,
                                value: data.terminal_id,
                            },
                        }));
                    }
                }
            })
            .catch((error) => processError('Не удалось получить сервисы оплаты', error));
    }, []);

    /**
     * Updates default_creditable parameter on terminal_id change.
     * Because we can offer credit only for Yookassa terminals.
     */
    React.useEffect(() => {
        if (!isCreditAvailable()) {
            handleDefaultCreditableChange(false);
        }
    }, [settings.terminal_id]);

    /**
     * Prepare additional fields components for render.
     */
    const additionalFieldsList = additionalFields.map((field, i) => {
        return (
            <AdditionalField
                key={'additional-field-' + i}
                fieldId={i}
                label={field?.field_name || ''}
                onChangeLabel={handleChangeLabel}
                onDelete={handleDeleteField}
            />
        );
    });

    return (
        <>
            {snack}
            <Panel
                contentStyles={{ minWidth: 420, maxWidth: 420, padding: '0 0.5rem', boxSizing: 'border-box' }}
                header={
                    <PanelHeader
                        title={isRecurrent ? t('products.type.subscription') : t('products.type.payment')}
                        back={true}
                        onBackClick={onBackClick}
                        after={
                            <IconButton accent={true} loading={loading} onClick={handleDoneClick} icon={<Check24 />} />
                        }
                    />
                }
            >
                <FormGroup title={t('productConstructor.section.general')}>
                    <FormItem
                        label={t('productConstructor.field.paymentDescription.label')}
                        required={true}
                        error={settings.title.error}
                    >
                        <Input
                            name="title"
                            type="text"
                            value={settings.title.value}
                            onChange={handleInputChange}
                            placeholder={t('productConstructor.field.paymentDescription.placeholder')}
                        />
                    </FormItem>
                    <FormItem
                        label={t('productConstructor.field.regularAmount.label')}
                        required={true}
                        error={settings.default_regular_amount.error}
                    >
                        <Input
                            name="default_regular_amount"
                            type="text"
                            value={settings.default_regular_amount.value}
                            onChange={handleInputChange}
                        />
                    </FormItem>
                    {isRecurrent && (
                        <FormItem
                            label={t('productConstructor.field.firstAmount.label')}
                            error={settings.default_first_amount.error}
                        >
                            <Input
                                name="default_first_amount"
                                type="text"
                                value={settings.default_first_amount.value}
                                onChange={handleInputChange}
                            />
                        </FormItem>
                    )}
                </FormGroup>

                <FormGroup title="Приём платежей">
                    {paymentServices !== null ? (
                        <>
                            <FormItem label={'Терминал'} error={settings.terminal_id.error} required={true}>
                                <Select
                                    options={paymentServices}
                                    disabled={!isCreatingNewPage}
                                    onChange={handleTerminalChange}
                                    defaultValue={settings.terminal_id.value}
                                />
                            </FormItem>

                            {isCreditAvailable() ? (
                                <Checkbox
                                    defaultChecked={data.default_creditable}
                                    onCheck={handleDefaultCreditableChange}
                                >
                                    Предлагать рассрочку при покупке
                                </Checkbox>
                            ) : null}
                        </>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Loader dark={true} />
                        </div>
                    )}
                </FormGroup>

                {isRecurrent && (
                    <FormGroup title={t('productConstructor.section.payments')}>
                        <FormItem label={t('productConstructor.field.numberOfPayments.label')} required>
                            <Select
                                options={paymentsCounts}
                                onChange={handlePaymentsLimit}
                                defaultValue={settings.is_unlimited.value ? 'inf' : 'specified'}
                            />
                        </FormItem>

                        {settings.is_unlimited.value === false && (
                            <FormItem required error={settings.default_payments_number.error}>
                                <Input
                                    type="text"
                                    name="default_payments_number"
                                    onChange={handleInputChange}
                                    value={settings.default_payments_number.value}
                                />
                            </FormItem>
                        )}

                        <FormItem label={t('productConstructor.field.paymentPeriod.label')} required>
                            <Select
                                options={paymentsPeriods}
                                onChange={handlePaymentsPeriodChange}
                                defaultValue={settings.once_per_days.value}
                            />
                        </FormItem>
                    </FormGroup>
                )}

                {/* Additional Input Fields. */}
                <FormGroup
                    title={t('productConstructor.section.externalFields.title')}
                    description={t('productConstructor.section.externalFields.description')}
                >
                    {additionalFieldsList}
                    <FormItem>
                        <Button
                            level={'secondary'}
                            text={t('productConstructor.section.externalFields.action.add')}
                            onClick={handleAddField}
                            icon={<Add24 />}
                        />
                    </FormItem>
                </FormGroup>

                <FormGroup title={t('productConstructor.section.information')} style={{ paddingBottom: 24 }}>
                    <FormItem
                        label={t('productConstructor.field.definition.label')}
                        required={true}
                        error={settings.definition.error}
                    >
                        <Input
                            name="definition"
                            type="text"
                            value={settings.definition.value}
                            onChange={handleInputChange}
                        />
                    </FormItem>
                    <FormItem label={t('productConstructor.field.paymentObject.label')}>
                        <Select
                            options={paymentsTypes}
                            defaultValue={settings.payment_object.value}
                            onChange={handlePaymentsTypeChange}
                        />
                    </FormItem>
                </FormGroup>
            </Panel>
            <Panel separated={true} style={previewPanelStyle} fill={true}>
                <PaymentForm
                    data={{
                        title: settings.title.value,
                        is_recurrent: isRecurrent,
                        external_fields: _prepareAdditionalFields(),
                        default_regular_amount: settings.default_regular_amount.value,
                        default_first_amount: settings.default_first_amount.value,
                        payment_object: settings.payment_object.value,
                        once_per_days: settings.once_per_days.value,
                    }}
                    previewMode={true}
                />
            </Panel>
        </>
    );
}

PageConstructor.propTypes = {
    /**
     * Is this a subscription page.
     */
    isRecurrent: PropTypes.bool,
    /**
     * Preloaded page data.
     */
    data: PropTypes.object,

    onBackClick: PropTypes.func,

    ...WithProcessError.propTypes,
};

export default WithProcessError(PageConstructor);
